/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import EmailInfo from "./sections/EmailInfo";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PersonalInfo from "./sections/PersonalInfo";
import { ArrowLeft } from "lucide-react";

const formState = z.object({
  email: z.string().email(),
  code: z
    .string()
    .min(6, { message: "Verification code should be 6 characters" }),
  referral_code: z.string().optional(),
  country: z.string().min(1, { message: "Country is required" }),
  surname: z.string().min(1, { message: "Surname is required" }),
  first_name: z.string().min(1, { message: "First name is required" }),
  username: z.string().min(1, { message: "Username is required" }),
  password: z.string().min(6, { message: "Password should be 6 characters" }),
  phone: z.string().min(1, { message: "Phone number is required" }),
  source: z.string().optional(),
});

const Register = () => {
  const { referral_code } = useParams();
  const [active, setActive] = useState(0);
  type accountState = z.infer<typeof formState>;
  const form = useForm<accountState>({
    resolver: zodResolver(formState),
    mode: "onBlur",
  });

  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));

  const validSteps = [
    {
      id: "1",
      fields: ["email", "code", "country", "referral_code", "source"],
    },
    {
      id: "2",
      fields: ["username", "password", "surname", "first_name", "phone"],
    },
  ];

  const next = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const fields = validSteps[active].fields;
    const output = await form.trigger(fields as any, {
      shouldFocus: true,
    });
    if (!output) return;
    setActive((prev) => prev + 1);
  };

  return (
    <div>
      <Helmet>
        <title>Mybitstore - Sign Up</title>
        <meta name="title" content="Mybitstore - Sign Up" />
        <meta
          name="description"
          content="Sign up on Mybitstore and buy and sell Bitcoin and other cryptocurrencies."
        />
        <link rel="canonical" href="/auth/register" />
      </Helmet>
      <div>
        <h1 className="font-semibold text-2xl tracking-tight text-center mt-4  ">
          Create an account
        </h1>

        <p className="text-sm text-center text-gray-500 mt-4">
          Create an account in just two step
        </p>

        <div className="md:mt-5">
          {active === 0 && (
            <EmailInfo form={form} nextStep={next} referral={referral_code} />
          )}
          {active === 1 && <PersonalInfo form={form} />}
          {active > 0 && (
            <div className="w-full flex justify-center mt-5">
              <div className="flex items-center gap-1 cursor-pointer">
                <ArrowLeft className="h-5 w-5 text-blue-500" />
                <span
                  onClick={prevStep}
                  className="text-center text-blue-500 font-semibold text-sm"
                >
                  Go back
                </span>
              </div>
            </div>
          )}

          <div className="flex mt-2 md:mt-4 justify-center  ">
            <span className="text-center text-sm">
              Already have an account?{" "}
              <Link className="text-blue-500" to="/auth">
                Sign in here
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
